import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import logoImage from '../../assets/images/logo.png';
import titleLight from '../../assets/images/title_light.svg';
import { useNavHeight } from '../Context/NavHeightContext';
import { useAuth } from '../../hooks/useAuth';
import { FACTURES_VIEW_ROLES } from '../../constants/roles';

import * as S from './Nav.styles';

const useDebounce = <T,>(value: T, delay: number): T => {
	const [debouncedValue, setDebouncedValue] = useState<T>(value);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(timer);
		};
	}, [value, delay]);

	return debouncedValue;
};

const Nav = ({ headerHeight }: { headerHeight: number }) => {
	const { user } = useAuth();
	const facturesVisible = (() => {
		const roles = (user?.roles as readonly string[]) ?? [];
		return FACTURES_VIEW_ROLES.some((r) => roles.includes(r));
	})();
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const [isFixed, setIsFixed] = useState<boolean>(false);
	const [navHeight, setLocalNavHeight] = useState<number>(0);

	const { setNavHeight } = useNavHeight();

		const navBarContainerRef = useRef<HTMLDivElement>(null);
	const menuWrapperRef = useRef<HTMLDivElement>(null);
	const resizeTimeoutRef = useRef<number>();

	// Debounce nav height updates
	const debouncedNavHeight = useDebounce(navHeight, 100);

	const toggleMenu = useCallback(() => {
		setIsMenuOpen((prev) => !prev);
	}, []);

	// Navigation happens through the <Link>; this handler only closes the
	// mobile menu.
	const logoClickHandler = useCallback(() => {
		setIsMenuOpen(false);
	}, []);

	// Memoize the scroll threshold calculation
	const scrollThreshold = useMemo(
		() => headerHeight - debouncedNavHeight,
		[headerHeight, debouncedNavHeight]
	);

	// Memoize the click outside handler
	const handleClickOutside = useCallback((event: MouseEvent) => {
		if (
			menuWrapperRef.current &&
			!menuWrapperRef.current.contains(event.target as Node)
		) {
			setIsMenuOpen(false);
		}
	}, []);

	useEffect(() => {
		let lastScrollTime = 0;
		const scrollThrottle = 16; // ~60fps

		const handleScroll = () => {
			const now = Date.now();
			if (now - lastScrollTime >= scrollThrottle) {
				const scrollValue = document.documentElement.scrollTop;
				setIsFixed(scrollValue > scrollThreshold);
				lastScrollTime = now;
			}
		};

		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [scrollThreshold]);

	useEffect(() => {
		const currentElement = navBarContainerRef.current;

		const handleResize = (entries: ResizeObserverEntry[]) => {
			// Clear any pending timeout
			if (resizeTimeoutRef.current) {
				window.clearTimeout(resizeTimeoutRef.current);
			}

			// Debounce the resize handler
			resizeTimeoutRef.current = window.setTimeout(() => {
				if (entries[0]) {
					const newHeight = entries[0].contentRect.height;
					setLocalNavHeight(newHeight);
					setNavHeight(newHeight);
				}
			}, 100);
		};

		const observer = new ResizeObserver(handleResize);

		if (currentElement) {
			observer.observe(currentElement);
		}

		return () => {
			if (currentElement) {
				observer.unobserve(currentElement);
			}
			if (resizeTimeoutRef.current) {
				window.clearTimeout(resizeTimeoutRef.current);
			}
		};
	}, [setNavHeight]);

	// Click outside handler effect
	useEffect(() => {
		if (isMenuOpen) {
			document.addEventListener('click', handleClickOutside);
		}

		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, [isMenuOpen, handleClickOutside]);

	return (
		<S.NavBarContainer $isFixed={isFixed} ref={navBarContainerRef}>
			<S.NavBarContainerBackground $isFixed={isFixed} />
			<S.NavBar>
				<S.LogoContainer
					as={Link}
					to="/inici"
					onClick={logoClickHandler}
					aria-label="Anar a l'inici"
				>
					<S.StyledLogo
						$isFixed={isFixed}
						src={logoImage}
						alt="AEIG Sant Narcís"
					/>
					<S.StyledTitleImage
						$isFixed={isFixed}
						src={titleLight}
						alt="AEIG Sant Narcís"
					/>
				</S.LogoContainer>

				<S.StyledNav>
					<S.StyledUL $isFixed={isFixed}>
						<li>
							<S.StyledLink to="/">Inici</S.StyledLink>
						</li>
						{/* <li>
							<S.StyledLink to="/unitats">Unitats</S.StyledLink>
						</li> */}
						<li>
												<S.StyledLink to="/galeria">
													Galeria
												</S.StyledLink>						</li>
						<li>
							<S.StyledLink to="/posts">
								Publicacions
							</S.StyledLink>
						</li>
						{facturesVisible && (
						<li>
							<S.StyledLink to="/factures">
								Factures
							</S.StyledLink>
						</li>
						)}
						<li>
							<S.StyledLink to="/calendari">
								Calendari
							</S.StyledLink>
						</li>
						<li>
							<S.StyledLink to="/contacte">Contacte</S.StyledLink>
						</li>
						<li>
							<S.StyledLink to="/rocacorba">
								Rocacorba
							</S.StyledLink>
						</li>
					</S.StyledUL>

					<S.MenuWrapper ref={menuWrapperRef}>
						<S.StyledHamburgerButton onClick={toggleMenu} />
						<S.StyledNavigationMenu
							$isOpen={isMenuOpen}
							onClose={() => setIsMenuOpen(false)}
						/>
					</S.MenuWrapper>
				</S.StyledNav>
			</S.NavBar>
		</S.NavBarContainer>
	);
};

export default Nav;
